/**
 * Ajustes do robô (spec 2026-09-24-robo-painel-de-ajustes). A tela escolhe sozinha
 * o canal — a USB casada pelo MAC, ou a rede quando o robô sabe se ajustar por ela
 * — e todo ajuste passa primeiro pelo Console, que registra na trilha. Pela USB,
 * a linha só vai para a placa depois do 200 (spec 7).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Alert, Button, Group, Loader, Radio, Stack, Text } from '@mantine/core'
import { IconArrowLeft, IconUsb } from '@tabler/icons-react'
import { Link, useParams } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'

import { Callout, DataCard } from '@/components/ui'
import {
  CHAVE_AJUSTES, CHAVE_DETALHE, alterarAjustes, codigoDaRecusa, enderecoDoWifiDaFrota,
  reciboDaFrota, reiniciarRobo, restaurarAjustes, useAjustesDoRobo, useRoboDetalhe,
  useWifiDoRobo, vincularPlacaAoCadastro, type AvisoDoAjuste, type MudancaDeAjustes,
  type RoboFrota,
} from '@/api/robo'
import type { Diag } from '@/features/robo-usb/protocolo'
import {
  aplicarPelaRede, aplicarPelaUsb, lerDiagPelaUsb, reiniciarPelaRede, reiniciarPelaUsb,
  restaurarPelaRede, restaurarPelaUsb, type ApiDeAjustes, type ResultadoDoAjuste,
} from '@/features/robo-ajustes/aplicar'
import { duracao, escolherCanal, motivoSemRede, redeServe, type EstadoDaRede } from '@/features/robo-ajustes/canal'
import { duasConexoes } from '@/features/robo-ajustes/conexoes'
import { DuasConexoes } from '@/features/robo-ajustes/DuasConexoes'
import {
  mensagemDoErro, mensagemDoReinicio, mensagemDoResultado, situacaoDaTela, type Mensagem,
} from '@/features/robo-ajustes/mensagens'
import { SecaoDiagnostico } from '@/features/robo-ajustes/SecaoDiagnostico'
import { SecaoManutencao } from '@/features/robo-ajustes/SecaoManutencao'
import { SecaoOperacao } from '@/features/robo-ajustes/SecaoOperacao'
import { SecaoTela } from '@/features/robo-ajustes/SecaoTela'
import { useAgora } from '@/features/robo-ajustes/useAgora'
import { useCanalUsb, type EstadoDaUsb } from '@/features/robo-ajustes/useCanalUsb'
import { nomeOuFallback } from './nomeDoRobo'
import { ReciboDoAviso } from './ReciboDoAviso'
import { rotuloDoRobo } from './avisoDoAjuste'
import { WifiDoRobo } from './WifiDoRobo'

export function AjustesDoRobo() {
  const { publicId } = useParams()
  const { data: robo, isLoading } = useRoboDetalhe(publicId)
  if (isLoading) return <Text size="sm" c="dimmed">Carregando…</Text>
  if (!robo) return <Text size="sm" c="dimmed">Robô não encontrado.</Text>
  if (robo.status === 'retired') {
    return <Text size="sm" c="dimmed">Robô aposentado não recebe ajuste.</Text>
  }
  return <Painel robo={robo} />
}

type Secao = 'tela' | 'operacao' | 'manutencao'

function Painel({ robo }: { robo: RoboFrota }) {
  const qc = useQueryClient()
  const agora = useAgora()
  const consulta = useAjustesDoRobo(robo.public_id)
  const ajustes = consulta.data
  // O nome da rede em uso sai do relato do Wi-Fi: a mesma consulta (e o mesmo cache)
  // do cartão Wi-Fi abaixo, então não custa uma ida a mais ao Console.
  const { data: wifi } = useWifiDoRobo(enderecoDoWifiDaFrota(robo.public_id))

  const vincular = useCallback(async (mac: string) => {
    try {
      await vincularPlacaAoCadastro(robo.public_id, mac)
      qc.invalidateQueries({ queryKey: CHAVE_DETALHE(robo.public_id) })
      return null
    } catch (erro) {
      return codigoDaRecusa(erro) ?? 'erro'
    }
  }, [qc, robo.public_id])
  const usb = useCanalUsb({ macDoCadastro: robo.mac ?? null, vincular })

  const rede: EstadoDaRede = {
    ativo: robo.estado === 'ativo',
    capaz: ajustes?.capaz ?? null,
    identidadePropria: (ajustes?.capaz ?? null) !== null,
  }
  const serveRede = redeServe(rede)
  const usbConectada = usb.estado.fase === 'conectada'
  const [preferido, setPreferido] = useState<'usb' | 'rede' | null>(null)
  const automatico = escolherCanal({ usb: usbConectada, rede })
  const canal = preferido === 'rede' && serveRede ? 'rede'
    : preferido === 'usb' && usbConectada ? 'usb' : automatico
  const ultimoContatoS = robo.last_seen_at ? (agora - Date.parse(robo.last_seen_at)) / 1000 : null

  // Um ajuste por vez (spec 7): a porta tem uma fila de linhas só, e duas
  // perguntas ao mesmo tempo leriam a resposta uma da outra.
  const [ocupado, setOcupado] = useState(false)
  const trava = useRef(false)
  const executar = useCallback(async (fn: () => Promise<void>) => {
    if (trava.current) return
    trava.current = true
    setOcupado(true)
    try { await fn() } finally { trava.current = false; setOcupado(false) }
  }, [])

  const [mensagens, setMensagens] = useState<Partial<Record<Secao, Mensagem>>>({})
  const [aviso, setAviso] = useState<{ secao: Secao; aviso: AvisoDoAjuste } | null>(null)
  const [aplicadoPelaUsb, setAplicadoPelaUsb] = useState<number | null>(null)
  const [salvo, setSalvo] = useState<{ versao: number; em: number } | null>(null)
  const [diagUsb, setDiagUsb] = useState<{ diag: Diag; em: number } | null>(null)

  const api: ApiDeAjustes = useMemo(() => ({
    alterar: (m, c) => alterarAjustes(robo.public_id, m, c),
    restaurar: (c) => restaurarAjustes(robo.public_id, c),
    reiniciar: (c) => reiniciarRobo(robo.public_id, c),
  }), [robo.public_id])

  const recarregar = () => qc.invalidateQueries({ queryKey: CHAVE_AJUSTES(robo.public_id) })

  const registrarResultado = (secao: Secao, r: ResultadoDoAjuste, operacao = false) => {
    setMensagens((m) => ({ ...m, [secao]: mensagemDoResultado(r, operacao) }))
    setSalvo({ versao: r.versao, em: Date.now() })
    if (r.tipo === 'aplicado') setAplicadoPelaUsb(r.versao)
    setAviso(r.resposta.aviso ? { secao, aviso: r.resposta.aviso } : null)
    qc.setQueryData(CHAVE_AJUSTES(robo.public_id), (a: typeof ajustes) =>
      (a ? { ...a, desejado: r.resposta.desejado } : a))
    void recarregar()
  }

  const ajustar = (secao: Secao, mudancas: MudancaDeAjustes, operacao = false) => executar(async () => {
    setMensagens((m) => ({ ...m, [secao]: undefined }))
    try {
      const r = canal === 'usb' && usb.linhas
        ? await aplicarPelaUsb({ api, porta: usb.linhas }, mudancas)
        : await aplicarPelaRede(api, mudancas)
      registrarResultado(secao, r, operacao)
    } catch (erro) {
      setMensagens((m) => ({ ...m, [secao]: mensagemDoErro(erro) }))
    }
  })

  const restaurar = () => executar(async () => {
    setMensagens((m) => ({ ...m, manutencao: undefined }))
    try {
      const r = canal === 'usb' && usb.linhas
        ? await restaurarPelaUsb({ api, porta: usb.linhas })
        : await restaurarPelaRede(api)
      registrarResultado('manutencao', r)
    } catch (erro) {
      setMensagens((m) => ({ ...m, manutencao: mensagemDoErro(erro) }))
    }
  })

  const reiniciar = () => executar(async () => {
    setMensagens((m) => ({ ...m, manutencao: undefined }))
    try {
      const r = canal === 'usb' && usb.linhas
        ? await reiniciarPelaUsb({ api, porta: usb.linhas })
        : await reiniciarPelaRede(api)
      setMensagens((m) => ({ ...m, manutencao: mensagemDoReinicio(r) }))
      void recarregar()
    } catch (erro) {
      setMensagens((m) => ({ ...m, manutencao: mensagemDoErro(erro) }))
    }
  })

  const lerDiagnostico = useCallback(() => executar(async () => {
    const linhas = usb.linhas
    if (!linhas) { await consulta.refetch(); return }
    const d = await lerDiagPelaUsb(linhas)
    if (d) setDiagUsb({ diag: d, em: Date.now() })
  }), [executar, usb.linhas, consulta])

  // Ao conectar pela USB, um DIAG só (spec 4.1): é leitura, não passa pelo Console.
  const conectadaAgora = usbConectada
  useEffect(() => {
    if (conectadaAgora) void lerDiagnostico()
    else setDiagUsb(null)
    // Só na borda da conexão: reler a cada render mandaria DIAG sem parar.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conectadaAgora])

  if (consulta.isLoading) return <Text size="sm" c="dimmed">Carregando os ajustes…</Text>
  if (!ajustes) return <Text size="sm" c="dimmed">Não foi possível ler os ajustes deste robô.</Text>

  const somenteLeitura = canal === 'nenhum'
  // Operação vale no servidor, para qualquer firmware (spec 4.3): basta o robô
  // falar com credencial própria, mesmo sem canal para a Tela.
  const operacaoLiberada = !somenteLeitura || rede.identidadePropria
  const motivo = motivoSemRede(rede, ultimoContatoS)
  const salvoEm = salvo && salvo.versao === ajustes.desejado.versao ? salvo.em : null
  const nome = nomeOuFallback(robo)
  const lidaEm = ajustes.diag ? consulta.dataUpdatedAt - ajustes.diag.idade_s * 1000 : null

  const reciboDe = (secao: Secao) => aviso && aviso.secao === secao && (
    <ReciboDoAviso key={aviso.aviso.id ?? 'sem-id'} aviso={aviso.aviso}
      caminho={aviso.aviso.id ? reciboDaFrota(robo.public_id, aviso.aviso.id) : null}
      robo={rotuloDoRobo(robo.hardware_model)} />
  )

  return (
    <Stack gap="md">
      <Group justify="space-between" align="flex-start" wrap="wrap">
        <Button variant="subtle" leftSection={<IconArrowLeft size={16} />} component={Link}
          to={`/robo/frota/${encodeURIComponent(robo.public_id)}`}>
          {nome}
        </Button>
      </Group>

      <DataCard title="Conexão">
        <Stack gap="sm">
          <DuasConexoes conexoes={duasConexoes({
            ultimoContato: robo.last_seen_at, diag: ajustes.diag, recusa: ajustes.recusa,
            redes: wifi?.relato?.redes, agora,
          })} />
          <Radio.Group label="Via" value={canal === 'nenhum' ? '' : canal}
            onChange={(v) => setPreferido(v as 'usb' | 'rede')}>
            <Group gap="lg" mt={4}>
              <Radio value="usb" disabled={!usbConectada}
                label={usbConectada ? 'USB (conectado)' : 'USB (não conectado)'} />
              <Radio value="rede" disabled={!serveRede}
                label={serveRede && ultimoContatoS !== null
                  ? `Rede (online há ${duracao(ultimoContatoS)})` : `Rede (${motivo})`} />
            </Group>
          </Radio.Group>
          <EstadoDaPorta estado={usb.estado} nome={nome} ocupado={ocupado}
            aoConectar={usb.conectarPeloSeletor} aoConfirmar={usb.confirmar} aoRecusar={usb.recusar} />
          {somenteLeitura && (
            <Callout variant="info">
              Sem canal para ajustar agora: a rede não serve ({motivo}) e não há robô conectado pela USB.
              Os valores abaixo são os salvos no Console.
            </Callout>
          )}
        </Stack>
      </DataCard>

      <DataCard title="Wi-Fi">
        <WifiDoRobo robo={robo} endereco={enderecoDoWifiDaFrota(robo.public_id)}
          semSinal={robo.estado !== 'ativo' && robo.estado !== 'atrasado'} />
      </DataCard>

      <DataCard title="Tela">
        <Stack gap="sm">
          <SecaoTela ajustes={ajustes} hardwareModel={robo.hardware_model} avatar={robo.avatar}
            podeAjustar={!somenteLeitura} ocupado={ocupado}
            situacao={situacaoDaTela(ajustes, aplicadoPelaUsb, salvoEm, agora)}
            mensagem={mensagens.tela ?? null} aoAplicar={(m) => ajustar('tela', m)} />
          {reciboDe('tela')}
        </Stack>
      </DataCard>

      <DataCard title="Operação">
        <Stack gap="sm">
          <SecaoOperacao ajustes={ajustes} podeAjustar={operacaoLiberada} ocupado={ocupado}
            mensagem={mensagens.operacao ?? null} aoSalvar={(m) => ajustar('operacao', m, true)} />
          {reciboDe('operacao')}
        </Stack>
      </DataCard>

      <DataCard title="Diagnóstico">
        <SecaoDiagnostico
          usb={usb.estado.fase === 'conectada'
            ? { info: usb.estado.info, diag: diagUsb?.diag ?? null, diagEm: diagUsb?.em ?? null, recursos: usb.recursos }
            : null}
          rede={ajustes.diag} redeLidaEm={lidaEm}
          cadastro={{ firmware_version: robo.firmware_version, mac: robo.mac }}
          log={usb.log} versaoDoLog={usb.versaoDoLog} agora={agora}
          ocupado={ocupado} aoLerDeNovo={lerDiagnostico} />
      </DataCard>

      <DataCard title="Manutenção">
        <SecaoManutencao nome={nome} publicId={robo.public_id} podeAgir={!somenteLeitura}
          ocupado={ocupado} mensagem={mensagens.manutencao ?? null}
          aoReiniciar={reiniciar} aoRestaurar={restaurar} />
      </DataCard>
    </Stack>
  )
}

function EstadoDaPorta({ estado, nome, ocupado, aoConectar, aoConfirmar, aoRecusar }: {
  estado: EstadoDaUsb
  nome: string
  ocupado: boolean
  aoConectar: () => void
  aoConfirmar: () => void
  aoRecusar: () => void
}) {
  const botao = (
    <Button variant="default" leftSection={<IconUsb size={16} />} onClick={aoConectar} disabled={ocupado}>
      Conectar pela USB
    </Button>
  )
  switch (estado.fase) {
    case 'sem_web_serial':
      return (
        <Text size="sm" c="dimmed">
          Ajustar pela USB funciona no Chrome ou no Edge, no computador. Este navegador não dá acesso à porta USB.
        </Text>
      )
    case 'procurando':
      return (
        <Group gap="xs">
          <Loader size="xs" />
          <Text size="sm">Procurando o robô nas portas USB já autorizadas neste navegador…</Text>
        </Group>
      )
    case 'conectada':
      return (
        <Text size="xs" c="dimmed" ff="monospace">
          {`${estado.info.perfil} · ${estado.info.versao} · ${estado.info.mac}`}
        </Text>
      )
    case 'confirmar':
      return (
        <Alert color="yellow" title="Qual robô está no cabo?">
          <Stack gap="sm">
            <Text size="sm">
              O cadastro deste robô ainda não tem a placa. Esta placa (<Text span ff="monospace" size="sm">{estado.mac}</Text>)
              é o {nome}? Confirmar grava a placa no cadastro, e isso fica na trilha.
            </Text>
            <Group gap="sm">
              <Button size="xs" onClick={aoConfirmar} disabled={ocupado}>Sim, é este robô</Button>
              <Button size="xs" variant="default" onClick={aoRecusar} disabled={ocupado}>Não</Button>
            </Group>
          </Stack>
        </Alert>
      )
    case 'escolher':
      return (
        <Stack gap="xs">
          <Text size="sm">
            Há {estado.quantas} placas autorizadas neste navegador e o cadastro deste robô ainda não tem a placa.
            Nenhuma foi aberta: escolha a deste robô.
          </Text>
          <Group>{botao}</Group>
        </Stack>
      )
    case 'ausente':
      return (
        <Stack gap="xs">
          {estado.aviso && <Alert color="yellow">{estado.aviso}</Alert>}
          <Group>{botao}</Group>
        </Stack>
      )
  }
}
